import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardApi } from "@/services/api";

const urgencyConfig = {
  critical: { label: "Critical", color: "#ef4444", bg: "#fef2f2", dot: "#ef4444" },
  high:     { label: "High",     color: "#d97706", bg: "#fffbeb", dot: "#f59e0b" },
  medium:   { label: "Medium",   color: "#b45309", bg: "#fef3c7", dot: "#fbbf24" },
  low:      { label: "Low",      color: "#92400e", bg: "#fef9ee", dot: "#fcd34d" },
};

interface DeadlineItem {
  id: number;
  jobNo: string;
  title: string;
  client: string;
  dueDate: string;
  progress: number;
  urgency: string;
}

function getDaysLeft(dueDate: string) {
  const due = new Date(dueDate);
  const now = new Date();
  return Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export default function DeadlineAlerts() {
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState<DeadlineItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeadlines = async () => {
      try {
        const result = await dashboardApi.getStats();
        if (result?.upcomingDeadlinesList && Array.isArray(result.upcomingDeadlinesList)) {
          setDeadlines(result.upcomingDeadlinesList);
        } else {
          setDeadlines([]);
        }
      } catch {
        setDeadlines([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDeadlines();
  }, []);

  return (
    <div className="bg-white rounded-xl border border-amber-100/60 p-4 md:p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h3 className="font-semibold text-gray-900 text-base">Deadline Alerts</h3>
          <p className="text-xs text-gray-500 mt-0.5">Upcoming job deadlines</p>
        </div>
        <span className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: "#fef3c7" }}>
          <i className="ri-alarm-warning-line text-base" style={{ color: "#d97706" }} />
        </span>
      </div>

      {/* List */}
      <div className="flex-1 space-y-2.5 overflow-y-auto">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="p-3 rounded-xl border border-amber-100/40 animate-pulse" style={{ background: "#fffbeb" }}>
              <div className="h-3 rounded w-3/4 mb-2" style={{ background: "#fef3c7" }} />
              <div className="h-2.5 rounded w-1/2 mb-2" style={{ background: "#fef3c7" }} />
              <div className="h-1.5 rounded w-full" style={{ background: "#fef3c7" }} />
            </div>
          ))
        ) : deadlines.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: "#fef3c7" }}>
              <i className="ri-calendar-check-line text-xl" style={{ color: "#d97706" }} />
            </div>
            <p className="text-sm font-medium text-gray-600">No upcoming deadlines</p>
            <p className="text-xs text-gray-400 mt-1">All jobs are on track</p>
          </div>
        ) : (
          deadlines.map((d) => {
            const cfg = urgencyConfig[d.urgency as keyof typeof urgencyConfig] || urgencyConfig.low;
            const daysLeft = getDaysLeft(d.dueDate);
            const isOverdue = daysLeft <= 0;
            return (
              <div
                key={d.id}
                onClick={() => navigate(isOverdue ? "/job-cards?filter=overdue" : `/job-cards/${d.id}`)}
                className="p-3 rounded-xl border cursor-pointer transition-all hover:shadow-sm"
                style={{ background: cfg.bg, borderColor: cfg.color + "30" }}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <p className="text-xs font-semibold text-gray-800 truncate">{d.title}</p>
                      {isOverdue && (
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white flex-shrink-0">
                          Overdue
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{d.client}</p>
                  </div>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0"
                    style={{ color: cfg.color, background: "rgba(255,255,255,0.8)" }}
                  >
                    {daysLeft <= 0 ? "Overdue!" : daysLeft === 1 ? "Tomorrow" : `${daysLeft}d left`}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 rounded-full h-1.5" style={{ background: "rgba(255,255,255,0.6)" }}>
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${d.progress}%`, background: cfg.color }} />
                  </div>
                  <span className="text-xs font-semibold" style={{ color: cfg.color }}>{d.progress}%</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.dot }} />
                  <span className="text-xs text-gray-500">{d.jobNo}</span>
                  <span className="text-gray-300 mx-1">·</span>
                  <span className="text-xs text-gray-500">
                    Due {new Date(d.dueDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <button
        onClick={() => navigate("/job-cards?filter=overdue")}
        className="mt-4 w-full text-center text-xs font-semibold cursor-pointer hover:underline flex-shrink-0 transition-colors"
        style={{ color: "#d97706" }}
      >
        View all deadlines →
      </button>
    </div>
  );
}